/**
 * sectionRegistry
 *
 * Maps every section renderer component key (sectionConfig.renderer.component)
 * to its React component and a buildProps factory.
 *
 * Entry shape:
 *   {
 *     Component      – the React component to render
 *     wrapDetails    – (default true) wrap with <AccordionDetails>
 *     buildProps(storeData, meta, extras, stores) → props object
 *     buildActions(extras, isExpanded) → ReactElement | null   (optional)
 *   }
 *
 * buildProps args:
 *   storeData  – result of resolveStoreData(sectionConfig, stores)
 *   meta       – { sectionName, isActive, isExpanded, fieldLayout, subFieldLayouts }
 *   extras     – all runtime props that are not store data (suggestions, recording, etc.)
 *   stores     – raw buildStores() map, available for multi-store sections (Examinations)
 *
 * Sections take two distinct flags, and they are NOT the same thing:
 *   activeSection:     isExpanded – section is open, so render its editable body.
 *                                   Several sections can be open at once.
 *   isFocusedSection:  isActive   – section the doctor last clicked; only ever one.
 *                                   Sections read the shared speech-recognition
 *                                   transcript, so this must gate dictation — keying it
 *                                   off isExpanded would type one dictation into every
 *                                   open section at once.
 */

import React from "react";
import ChiefComplaint from "../../Sections/ChiefComplaint";
import Examinations from "../../Sections/Examinations";
import Diagnosis from "../../Sections/Diagnosis";
import AdultVaccination from "../../Sections/AdultVaccination";
import Investigations from "../../Sections/Investigations";
import Procedures from "../../Sections/Procedures";
import Medications from "../../Sections/Medications";
import FollowUpReferralComp from "../../Sections/FollowUpReferralComp";
import MedicalAdvice from "../../Sections/MedicalAdvice";
import DrawingPad from "../../Utils/DrawingPad";
import Attachments from "../../Sections/Attachments";
import { StyledButton } from "../../StyledComponents";

// ─── shared prop-group helpers ────────────────────────────────────────────────

const voiceProps = (extras) => ({
  startRecording: extras.handleStartRecordingForIndividualSection,
  stopRecording: extras.handleStopRecordingForIndividualSection,
  isAiEnabled: extras.isAiEnabled,
});

const suggestionProps = (extras) => ({
  suggestions: extras.suggestions,
  aiSuggestionToggle: extras.aiSuggestionToggle,
  ruleSuggestionToggle: extras.ruleSuggestionToggle,
  handleCrossButtonClick: extras.handleCrossButtonClick,
});

/** Stable empty array — avoid `x || []` creating a new [] each render. */
const EMPTY_IPD_ADMISSION_REFERRAL = [];

// ─── registry ─────────────────────────────────────────────────────────────────

export const sectionRegistry = {
  ChiefComplaint: {
    Component: ChiefComplaint,
    wrapDetails: true,
    buildProps: (storeData, { sectionName, isActive, isExpanded, fieldLayout }, extras) => ({
      sectionName,
      activeSection: isExpanded,
      isFocusedSection: isActive,
      data: storeData.data,
      setData: storeData.setData,
      fieldLayout,
      ...suggestionProps(extras),
      ...voiceProps(extras),
    }),
  },

  Examinations: {
    Component: Examinations,
    wrapDetails: true,
    // Examinations binds to three separate stores: vitals, dentalData, bloodGroup.
    // We pull them directly from stores rather than from the single storeData binding.
    buildProps: (_storeData, { sectionName, isExpanded, fieldLayout }, extras, stores) => ({
      sectionName,
      activeSection: isExpanded,
      data: stores.vitals.data,
      setData: stores.vitals.setData,
      dentalData: stores.dentalData.data,
      setDentalData: stores.dentalData.setData,
      bloodGroup: stores.bloodGroup.data,
      setBloodGroup: stores.bloodGroup.setData,
      hasVitalValue: true,
      doctorData: extras.state?.doctorData,
      fieldLayout,
      ...suggestionProps(extras),
    }),
  },

  Diagnosis: {
    Component: Diagnosis,
    wrapDetails: true,
    buildProps: (storeData, { sectionName, isActive, isExpanded, fieldLayout }, extras) => ({
      sectionName,
      activeSection: isExpanded,
      isFocusedSection: isActive,
      data: storeData.data,
      setData: storeData.setData,
      fieldLayout,
      ...suggestionProps(extras),
      ...voiceProps(extras),
    }),
  },

  AdultVaccination: {
    Component: AdultVaccination,
    wrapDetails: true,
    buildProps: (storeData, { sectionName, isActive, isExpanded, fieldLayout }, extras) => ({
      sectionName,
      activeSection: isExpanded,
      isFocusedSection: isActive,
      data: storeData.data,
      setData: storeData.setData,
      fieldLayout,
      ...voiceProps(extras),
    }),
  },

  Investigations: {
    Component: Investigations,
    wrapDetails: true,
    buildProps: (storeData, { sectionName, isActive, isExpanded, fieldLayout }, extras) => ({
      sectionName,
      activeSection: isExpanded,
      isFocusedSection: isActive,
      data: storeData.data,
      setData: storeData.setData,
      fieldLayout,
      ...suggestionProps(extras),
      ...voiceProps(extras),
    }),
  },

  Procedures: {
    Component: Procedures,
    wrapDetails: true,
    buildProps: (storeData, { sectionName, isActive, isExpanded, fieldLayout }, extras) => ({
      sectionName,
      activeSection: isExpanded,
      isFocusedSection: isActive,
      data: storeData.data,
      setData: storeData.setData,
      fieldLayout,
      ...suggestionProps(extras),
      ...voiceProps(extras),
    }),
  },

  Medications: {
    Component: Medications,
    wrapDetails: true,
    buildProps: (storeData, { sectionName, isActive, isExpanded, fieldLayout }, extras, stores) => {
      // Reasons come from the cross-section dependency (Chief Complaints.condition).
      // Fall back to reading directly from the store when the dependency is absent.
      const medicationReasons =
        extras.sectionDependencies?.medicationReasons ??
        stores.selectedSectionsData.data?.["Chief Complaints"]?.map((d) => d.condition);
      const initialObservationData =
        extras.sectionDependencies?.initialObservationForMeds ??
        stores.selectedSectionsData.data?.InitialObservation;
      return {
        sectionName,
        activeSection: isExpanded,
        isFocusedSection: isActive,
        data: storeData.data,
        setData: storeData.setData,
        fieldLayout,
        reasonsOptions: medicationReasons,
        initialObservationData,
        routeAutoData: extras.routeAutoData,
        ...suggestionProps(extras),
        ...voiceProps(extras),
      };
    },
  },

  FollowUpReferral: {
    Component: FollowUpReferralComp,
    wrapDetails: true,
    // storeData has { FollowUp, setFollowUp, Referral, setReferral, IPDAdmissionReferral, setIPDAdmissionReferral } from dataBindings
    buildProps: (storeData, { sectionName, isExpanded, subFieldLayouts }, extras, stores) => ({
      sectionName,
      activeSection: isExpanded,
      followUpData: storeData.FollowUp,
      setFollowUpData: storeData.setFollowUp,
      referralData: storeData.Referral,
      setReferralData: storeData.setReferral,
      ipdReferralData: Array.isArray(storeData.IPDAdmissionReferral)
        ? storeData.IPDAdmissionReferral
        : EMPTY_IPD_ADMISSION_REFERRAL,
      setIpdReferralData:
        storeData.setIPDAdmissionReferral ||
        ((data) => stores?.selectedSectionsData?.setData?.("IPDAdmissionReferral", data)),
      subFieldLayouts,
      followUpError: extras.followUpError,
      referralError: extras.referralError,
      ipdReferralError: extras.ipdReferralError || {},
      setFollowUpError: extras.setFollowUpError,
      setReferralError: extras.setReferralError,
      setIpdReferralError: extras.setIpdReferralError,
      dateForAppointment: extras.state?.dateForAppointment,
    }),
  },

  MedicalAdvice: {
    Component: MedicalAdvice,
    wrapDetails: true,
    buildProps: (storeData, { sectionName, isExpanded, fieldLayout }) => ({
      sectionName,
      activeSection: isExpanded,
      data: storeData.data,
      setData: storeData.setData,
      fieldLayout,
    }),
  },

  DrawingPad: {
    Component: DrawingPad,
    // DrawingPad renders directly inside StyledAccordion — no AccordionDetails wrapper
    wrapDetails: false,
    buildProps: (storeData, { sectionName, isExpanded, fieldLayout }, extras) => ({
      sectionName,
      activeSection: isExpanded,
      data: storeData.data,
      setData: storeData.setData,
      doctorData: extras.state?.doctorData,
      fieldLayout,
    }),
  },

  Attachments: {
    Component: Attachments,
    wrapDetails: true,
    buildActions: (extras, isExpanded) =>
      isExpanded ? (
        <StyledButton
          variant="contained"
          size="small"
          bgColor="#0E3F66"
          hoverBgColor="#07367dff"
          onClick={(e) => {
            e.stopPropagation();
            extras.setIsS3ModalOpen(true);
          }}
          sx={{
            fontSize: { xs: "0.65rem", sm: "0.75rem" },
            fontWeight: 600,
            px: 1.2,
            py: 0.5,
            minWidth: "auto",
            lineHeight: 1.2,
          }}
        >
          Uploaded Files
        </StyledButton>
      ) : null,
    buildProps: (storeData, { sectionName, isExpanded, fieldLayout }, extras) => ({
      sectionName,
      activeSection: isExpanded,
      data: storeData.data,
      setData: storeData.setData,
      fieldLayout,
      patientId: extras.state?.patientData?.patient_id,
      facilityName:
        extras.state?.facility_display_name ||
        sessionStorage.getItem("facility_display_name"),
      orgCode: sessionStorage.getItem("ORG_CODE") || "ORGCXYHM",
      appMode: sessionStorage.getItem("APP_MODE") || "SaaS",
      isS3ModalOpen: extras.isS3ModalOpen,
      setIsS3ModalOpen: extras.setIsS3ModalOpen,
      ...suggestionProps(extras),
    }),
  },
};
